// License-state banner derivation — pure + unit-testable (mirrors
// navVisibility.ts house style). Derives the banner purely from the COMPUTED
// lifecycle + is_trial + tier the backend emits — NEVER the raw `status`
// column, which can be stale (e.g. an enterprise license left at status="trial"
// after an upgrade would otherwise render "trial expired" — the BOI bug).
import type { LicenseResponse } from "@/api/license";

export interface LicenseBannerState {
  msg: string;
  tone: "amber" | "red";
}

const TIER_LABEL: Record<string, string> = {
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
};

/** Returns the banner to show, or null for none. Cases:
 *  - lifecycle "active"  → null (healthy: paid or in-term trial — no banner).
 *  - lifecycle "grace"   → trial-grace OR paid-grace ("{Tier} license in grace
 *                          period — renew to avoid interruption", matching the
 *                          backend alert).
 *  - lifecycle "expired" → trial-expired (the original copy) OR paid-expired
 *                          ("{Tier} license expired — renew to restore write
 *                          access", the H-5 read-only state).
 */
export function licenseBannerState(
  license: LicenseResponse | null | undefined,
): LicenseBannerState | null {
  if (!license) return null;
  const tier = TIER_LABEL[license.tier] ?? "License";

  switch (license.lifecycle) {
    case "active":
      return null;
    case "grace":
      return license.is_trial
        ? {
            msg: "Your trial has ended — you're in a grace period. Upgrade now to keep adding cameras and users.",
            tone: "amber",
          }
        : {
            msg: `${tier} license in grace period — renew to avoid interruption.`,
            tone: "amber",
          };
    case "expired":
      return license.is_trial
        ? {
            msg: "Your trial has expired. Upgrade now to keep adding cameras and users.",
            tone: "red",
          }
        : {
            msg: `${tier} license expired — renew to restore write access.`,
            tone: "red",
          };
    default:
      return null;
  }
}
